import { useTranslation } from "next-i18next";
import { useState } from "react";
import { useAppDispatch } from "@/store/hooks";
import { setProductFilterVisible } from "@/store/mobilePopup";
import { AppstoreOutlined, BarsOutlined } from "@ant-design/icons";
import Icon from "../Icon";
import { onKeyActivate } from "@/utils/index";

export type LayoutMode = "grid" | "list";

/**
 * 排序筛选
 * @returns 排序筛选组件
 */
const SortFilter = ({
  onChange,
  layoutMode = "grid",
  onLayoutChange,
}: {
  onChange: (activeIndex: number, value: string) => void;
  layoutMode?: LayoutMode;
  onLayoutChange?: (mode: LayoutMode) => void;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const sortsData = [
    {
      text: "product_category.dream_value",
      value: "asc",
    },
    {
      text: "product_category.shelf_time",
      value: "asc",
    },
  ];

  const [sorts, setSorts] = useState(sortsData);
  const [activeIndex, setActiveIndex] = useState<number>(0);

  // 切换排序
  const handleSwitchSort = (index: number, value: string) => {
    if (activeIndex === index) {
      const data = JSON.parse(JSON.stringify(sorts));
      data[index].value = value === "asc" ? "desc" : "asc";
      setSorts(data);
      onChange(index, value === "asc" ? "desc" : "asc");
    } else {
      setActiveIndex(index);
      onChange(index, value);
    }
  };

  /**
   * open or close mobile filters popup
   * @param visible boolean popup visible
   */
  const handleFilterVisible = (visible: boolean) => {
    dispatch(setProductFilterVisible(visible));
  };

  // sort item
  const SortFilterItem = ({
    text,
    value,
    index,
  }: {
    text: string;
    value: string;
    index: number;
  }) => {
    return (
      <div
        className={`mr-4 flex cursor-pointer items-center rounded border-0 border-gray-100 px-0 py-0  text-[11px] transition-colors hover:text-primary 2xs:text-sm lg:border lg:px-6 lg:py-3 lg:text-base ${
          index === activeIndex ? "border-primary text-primary" : ""
        }`}
        onClick={() => {
          handleSwitchSort(index, value);
        }}
        role="button"
        tabIndex={0}
        onKeyDown={onKeyActivate(() => handleSwitchSort(index, value))}
        data-qt="mall_sort_click"
        data-qt-sort-field={text}
        data-qt-sort-order={value}
      >
        {t(`${text}_${value}`)}
        <Icon
          name={index === activeIndex ? "sort-up-red" : "sort-up"}
          size={16}
          title="sort"
          className={`ml-2 lg:ml-4 ${value === "desc" ? "rotate-180" : ""}`}
        />
      </div>
    );
  };

  return (
    <div className="sticky left-0 top-14 z-30 mb-0 flex items-center justify-between border-0 border-gray-100 bg-white pb-5 text-base font-semibold text-stone-800 lg:static lg:mb-[30px] lg:border-b lg:pb-4 lg:text-stone-300">
      <div className="flex flex-1 items-center">
        <div className="mr-4 hidden lg:block">
          {t("product_category.sort_type")}:
        </div>

        {sorts.map((sort, index) => (
          <SortFilterItem
            key={index}
            text={sort.text}
            value={sort.value}
            index={index}
          />
        ))}
      </div>
      {/* 布局切换（PC + Mobile） */}
      {onLayoutChange && (
        <div className="mx-2 flex items-center overflow-hidden rounded border border-gray-500 lg:ml-4 lg:rounded-lg">
          <div
            className={`flex cursor-pointer items-center justify-center px-1.5 py-1 text-xs transition-colors lg:px-3 lg:py-2 lg:text-base ${
              layoutMode === "grid" ? "bg-primary text-white" : "bg-white text-stone-300 hover:text-stone-500"
            }`}
            onClick={() => onLayoutChange("grid")}
            data-qt="mall_layout_switch"
            data-qt-layout="grid"
          >
            <AppstoreOutlined />
          </div>
          <div
            className={`flex cursor-pointer items-center justify-center px-1.5 py-1 text-xs transition-colors lg:px-3 lg:py-2 lg:text-base ${
              layoutMode === "list" ? "bg-primary text-white" : "bg-white text-stone-300 hover:text-stone-500"
            }`}
            onClick={() => onLayoutChange("list")}
            data-qt="mall_layout_switch"
            data-qt-layout="list"
          >
            <BarsOutlined />
          </div>
        </div>
      )}

      <div
        className="flex items-center text-xs 2xs:text-sm lg:hidden"
        onClick={() => {
          handleFilterVisible(true);
        }}
        data-qt="mall_filter_open"
      >
        {t("mobile.filter")}
        <Icon name="filter" isMobile size={16} title="sort" className="ml-2" />
      </div>
    </div>
  );
};

export default SortFilter;
